import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Send, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const NETWORKS = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "threads", label: "Threads" },
];

export const SocialPublish = ({ articleId, outlets, outletId, imageUrl, published }) => {
  const [outlet, setOutlet] = useState(outletId || (outlets || [])[0]?.id || "");
  const [nets, setNets] = useState(["facebook", "instagram"]);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const toggle = (id) => setNets((n) => (n.includes(id) ? n.filter((x) => x !== id) : [...n, id]));

  const publish = async () => {
    setBusy("pub");
    try {
      const { data } = await api.post(`/articles/${articleId}/social-publish`, {
        outlet_id: outlet, networks: nets, image_url: imageUrl || null,
      });
      setResult(data);
      if (data.results.length) toast.success(`Publicado en ${data.results.map((r) => r.network).join(", ")}`);
      (data.errors || []).forEach((e) => toast.error(`${e.network}: ${e.error}`));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const diagnose = async () => {
    setBusy("test");
    try {
      const { data } = await api.get(`/outlets/${outlet}/social-test`);
      setResult({ checks: data.checks });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border-t border-border mt-6 pt-5" data-testid="social-publish">
      <div className="kicker mb-1">Publicación automática en redes</div>
      <p className="text-xs text-muted-foreground mb-4">
        Publica la imagen y el texto directamente en la página de Facebook, la cuenta de Instagram
        profesional y Threads del medio. Configure los tokens en Medios → Redes con API.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-xs uppercase tracking-wider">Medio</Label>
          <select
            value={outlet}
            onChange={(e) => setOutlet(e.target.value)}
            className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
            data-testid="social-publish-outlet"
          >
            {(outlets || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Imagen que se publicará</Label>
          <p className="text-xs mono text-muted-foreground mt-2 truncate" data-testid="social-publish-image">
            {imageUrl ? imageUrl.split("/").slice(-1)[0] : "Portada de la noticia"}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {NETWORKS.map((n) => {
          const on = nets.includes(n.id);
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => toggle(n.id)}
              className={`text-sm px-3 py-1.5 border transition-colors ${on
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary/60"}`}
              data-testid={`social-net-${n.id}`}
            >
              {n.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={diagnose} disabled={!outlet || busy === "test"}
                data-testid="social-test-btn">
          {busy === "test" ? "Probando…" : "Probar conexión"}
        </Button>
        <Button onClick={publish} disabled={!outlet || !nets.length || busy === "pub"}
                data-testid="social-publish-btn">
          <Send className="h-4 w-4 mr-2" />{busy === "pub" ? "Publicando…" : "Publicar en redes"}
        </Button>
      </div>

      {result?.checks && (
        <ul className="mt-4 space-y-1.5" data-testid="social-test-result">
          {result.checks.map((c) => (
            <li key={c.name} className="flex items-start gap-2 text-xs">
              {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    : <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />}
              <span><strong>{c.name}:</strong> {c.detail}</span>
            </li>
          ))}
        </ul>
      )}
      {result?.results?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="social-publish-result">
          {result.results.map((r) => (
            <Badge key={r.network} variant="default">{r.network} · {r.id}</Badge>
          ))}
        </div>
      )}
      {(published || []).length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground" data-testid="social-published-log">
          Publicaciones previas: {published.map((p) => `${p.network} (${(p.at || "").slice(0, 10)})`).join(" · ")}
        </div>
      )}
    </div>
  );
};
